import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

let logoutTimer: number | undefined;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForToken = async (timeoutMs = 5000): Promise<string | null> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const token = sessionStorage.getItem("entra_token");
    if (token) {
      return token;
    }
    await wait(100);
  }
  return null;
};

export const clearAuthData = () => {
  sessionStorage.removeItem("entra_token");
  sessionStorage.removeItem("entra_email");
  sessionStorage.removeItem("entra_name");
  sessionStorage.removeItem("role");
  sessionStorage.removeItem("user_id");
  sessionStorage.removeItem("entra_token_expires_at");
  
  // Disconnect WebSocket on logout
  try {
    const { disconnectWebSocket } = require("../utils/websocketService");
    disconnectWebSocket();
  } catch (error) {
    // WebSocket service might not be loaded yet
  }
};

export const forceLogout = (reason = "expired") => {
  if (logoutTimer) {
    window.clearTimeout(logoutTimer);
    logoutTimer = undefined;
  }
  clearAuthData();
  sessionStorage.setItem("auth_reason", reason);
  window.location.href = `/auth?reason=${encodeURIComponent(reason)}`;
};

export const scheduleAutoLogout = (expiresAtMs?: number | string | null) => {
  if (logoutTimer) {
    window.clearTimeout(logoutTimer);
    logoutTimer = undefined;
  }
  if (!expiresAtMs) return;
  const expiresAt =
    typeof expiresAtMs === "string" ? Number(expiresAtMs) : Number(expiresAtMs);
  if (Number.isNaN(expiresAt)) return;

  const triggerAt = expiresAt - 60 * 1000; // logout 1 minute early
  const delay = triggerAt - Date.now();
  if (delay <= 0) {
    forceLogout("expired");
    return;
  }
  logoutTimer = window.setTimeout(() => {
    forceLogout("expired");
  }, delay);
};

// Create axios instance without static headers
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 second timeout
});

// Add request interceptor to dynamically add the current token
api.interceptors.request.use(
  async (config) => {
    let token = sessionStorage.getItem("entra_token");
    if (!token) {
      token = await waitForToken();
    }
    if (!token) {
      console.error("🔐 No entra_token available for request:", config.url);
      forceLogout("missing_token");
      throw new axios.Cancel("Auth token unavailable");
    }
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest?._retry
    ) {
      originalRequest._retry = true;
      forceLogout("expired");
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Utility function to check if user is authenticated
export const isAuthenticated = () => {
  const token = sessionStorage.getItem("entra_token");
  return !!token;
};